<template>
    <div class="advanced-stats-filters">
        <div class="advanced-stats-filters__header">
            <h3 class="advanced-stats-filters__title">{{ isDesktop ? 'Фильтры' : 'Матчи' }}</h3>
            <button
                v-if="isDesktop"
                type="button"
                class="advanced-stats-filters__toggle"
                @click="isExpanded = !isExpanded"
            >
                {{ isExpanded ? 'Свернуть' : 'Развернуть' }}
            </button>
            <button
                v-else
                type="button"
                class="advanced-stats-filters__toggle"
                @click="openDrawer"
            >
                Фильтры
            </button>
        </div>

        <div v-show="isDesktop ? isExpanded : true" class="advanced-stats-filters__content">
            <div class="advanced-stats-filters__body">
                <aside v-if="isDesktop" class="advanced-stats-filters__sidebar">
                    <FilterFields
                        v-model:search-query="searchQuery"
                        :draft-filters="draftFilters"
                        :team-options="teamOptions"
                        :position-options="positionOptions"
                        :all-teams-selected="allTeamsSelected"
                        :all-positions-selected="allPositionsSelected"
                        @toggle-all-teams="toggleAllTeams"
                        @toggle-all-positions="toggleAllPositions"
                        @competition-change="onCompetitionChange"
                        @apply="applyFilters"
                        @reset="resetFilters"
                    />
                </aside>

                <div class="matches-section">
                    <div class="matches-header">
                        <div class="matches-title">
                            Матчи
                            <span class="matches-count">({{ filteredMatches.length }} из {{ matches.length }})</span>
                        </div>
                        <div class="matches-actions">
                            <FilterSelectAllButton
                                :all-selected="allMatchesSelected"
                                :disabled="!filteredMatches.length"
                                @toggle="toggleAllMatches"
                            />
                        </div>
                    </div>
                    <p class="matches-hint">Если матчи не выбраны — учитываются все матчи по выбранным фильтрам.</p>
                    <div class="matches-list">
                        <label
                            v-for="match in filteredMatches"
                            :key="match.id"
                            class="match-item"
                            :class="{ 'match-item-selected': draftFilters.match_ids.includes(match.id) }"
                        >
                            <input type="checkbox" :value="match.id" v-model="draftFilters.match_ids" />
                            <div class="match-row">
                                <div class="match-date-cell">
                                    <div class="match-date">{{ formatMatchDate(match.date) }}</div>
                                </div>
                                <div class="match-teams-cell">
                                    <div class="match-teams">
                                        <span class="match-team">
                                            <span v-if="match.home_team_logo" class="match-team__logo">
                                                <img :src="match.home_team_logo" :alt="match.home_team" />
                                            </span>
                                            <span v-else class="match-team__logo match-team__logo--placeholder" />
                                            <span>{{ match.home_team }}</span>
                                        </span>
                                        <span class="match-teams__sep">—</span>
                                        <span class="match-team">
                                            <span v-if="match.away_team_logo" class="match-team__logo">
                                                <img :src="match.away_team_logo" :alt="match.away_team" />
                                            </span>
                                            <span v-else class="match-team__logo match-team__logo--placeholder" />
                                            <span>{{ match.away_team }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="match-badges-cell">
                                    <div v-if="match.score && match.score !== '0:0'" class="match-badge match-badge-score">
                                        {{ match.score }}
                                    </div>
                                    <img
                                        v-if="competitionIcon(match.competition_type)"
                                        :src="competitionIcon(match.competition_type)"
                                        :alt="competitionLabel(match.competition_type)"
                                        :title="competitionLabel(match.competition_type)"
                                        class="match-competition-icon"
                                    />
                                    <div
                                        v-else-if="competitionLabel(match.competition_type)"
                                        class="match-badge match-badge-competition"
                                    >
                                        {{ competitionLabel(match.competition_type) }}
                                    </div>
                                    <span class="match-stats-slot">
                                        <a
                                            v-if="match.has_stats"
                                            :href="gamePublicPath(match)"
                                            class="match-stats-icon"
                                            title="Статистика матча"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            @click.stop
                                        >
                                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                                <path d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z" fill="currentColor" />
                                            </svg>
                                        </a>
                                        <span
                                            v-else-if="match.is_technical_defeat"
                                            class="match-tp-badge"
                                            title="Техническое поражение"
                                        >ТП</span>
                                    </span>
                                </div>
                            </div>
                        </label>
                        <p v-if="!filteredMatches.length" class="filter-empty matches-empty">Нет матчей для выбранных фильтров</p>
                    </div>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div
                v-if="!isDesktop && isDrawerOpen"
                class="as-filters-drawer-overlay"
                @click.self="closeDrawer"
            >
                <aside class="as-filters-drawer" role="dialog" aria-modal="true" aria-label="Фильтры">
                    <header class="as-filters-drawer__header">
                        <h2>Фильтры</h2>
                        <button
                            type="button"
                            class="as-filters-drawer__close"
                            aria-label="Закрыть"
                            @click="closeDrawer"
                        >
                            ×
                        </button>
                    </header>
                    <div class="as-filters-drawer__body">
                        <FilterFields
                            v-model:search-query="searchQuery"
                            :draft-filters="draftFilters"
                            :team-options="teamOptions"
                            :position-options="positionOptions"
                            :all-teams-selected="allTeamsSelected"
                            :all-positions-selected="allPositionsSelected"
                            @toggle-all-teams="toggleAllTeams"
                            @toggle-all-positions="toggleAllPositions"
                            @competition-change="onCompetitionChange"
                            @apply="applyFiltersFromDrawer"
                            @reset="resetFiltersFromDrawer"
                        />
                    </div>
                </aside>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import FilterSelectAllButton from '@/components/advanced-stats/FilterSelectAllButton.vue';
import FilterFields from '@/components/advanced-stats/AdvancedStatsFilterFields.vue';
import { useMediaQuery } from '@/composables/useMediaQuery';
import {
    COMPETITION_ICONS,
    COMPETITION_TYPES,
    compactFilterSelection,
    expandFilterSelection,
    filterMatchesByDraft,
    getDefaultDraftFilters,
    getStagesForCompetitionTypes,
    parseStageFilterKey,
} from '@/utils/skillQualityConfig';
import { gamePublicPath } from '@/utils/gameUrl';

const props = defineProps({
    seasons: { type: Array, default: () => [] },
    teams: { type: Array, default: () => [] },
    positions: { type: Array, default: () => [] },
    matches: { type: Array, default: () => [] },
    appliedFilters: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['apply', 'reset', 'search-change']);

const isDesktop = useMediaQuery('(min-width: 900px)');
const isExpanded = ref(true);
const isDrawerOpen = ref(false);
const isSyncingFromApplied = ref(false);
const isEnablingCompetitionFromStage = ref(false);

const draftFilters = ref(createExpandedDraft());
const searchQuery = ref('');

const teamOptions = computed(() =>
    props.teams.map((team) => ({
        value: team.id,
        label: team.name,
        logo: team.logo_path,
    }))
);

const positionOptions = computed(() =>
    props.positions.map((position) => ({
        value: position.slug,
        label: position.name,
    }))
);

const filteredMatches = computed(() =>
    filterMatchesByDraft(props.matches, props.appliedFilters, draftFilters.value)
);

const allMatchesSelected = computed(() =>
    isAllSelected(
        draftFilters.value.match_ids,
        filteredMatches.value.map((match) => match.id)
    )
);

const allTeamsSelected = computed(() =>
    isAllSelected(
        draftFilters.value.team_ids,
        teamOptions.value.map((option) => Number(option.value))
    )
);

const allPositionsSelected = computed(() =>
    isAllSelected(
        draftFilters.value.position_slugs,
        positionOptions.value.map((option) => option.value)
    )
);

function isAllSelected(selected, allIds) {
    return (
        allIds.length > 0 &&
        allIds.every((id) => selected.some((item) => String(item) === String(id)))
    );
}

function toggleAllMatches() {
    draftFilters.value.match_ids = allMatchesSelected.value
        ? []
        : filteredMatches.value.map((match) => match.id);
}

function toggleAllTeams() {
    draftFilters.value.team_ids = allTeamsSelected.value
        ? []
        : teamOptions.value.map((option) => Number(option.value));
}

function toggleAllPositions() {
    draftFilters.value.position_slugs = allPositionsSelected.value
        ? []
        : positionOptions.value.map((option) => option.value);
}

function createExpandedDraft() {
    return expandFilterSelection(props.appliedFilters, {
        teams: props.teams,
        positions: props.positions,
        matches: props.matches,
        appliedFilters: props.appliedFilters,
    });
}

function syncDraftFromApplied() {
    isSyncingFromApplied.value = true;
    draftFilters.value = createExpandedDraft();
    isSyncingFromApplied.value = false;
}

watch(
    () => [props.appliedFilters, props.teams, props.positions, props.matches],
    () => {
        syncDraftFromApplied();
    },
    { deep: true }
);

watch(searchQuery, (value) => {
    emit('search-change', value);
});

watch(
    () => [...draftFilters.value.competition_types],
    (newTypes, oldTypes) => {
        if (isSyncingFromApplied.value || isEnablingCompetitionFromStage.value || !oldTypes?.length) {
            return;
        }

        const oldAvailable = getStagesForCompetitionTypes(oldTypes).map((stage) => stage.value);
        const newAvailable = getStagesForCompetitionTypes(newTypes).map((stage) => stage.value);
        const hadAllStages =
            oldAvailable.length > 0 && oldAvailable.every((stage) => draftFilters.value.stages.includes(stage));

        if (hadAllStages) {
            draftFilters.value.stages = newAvailable;
        } else {
            draftFilters.value.stages = draftFilters.value.stages.filter((stage) => newAvailable.includes(stage));
        }

        syncMatchIdsAfterFilterChange(oldTypes, oldAvailable);
    }
);

watch(
    () => [...draftFilters.value.stages],
    (newStages, oldStages) => {
        if (isSyncingFromApplied.value || !oldStages?.length) {
            return;
        }

        const addedStages = newStages.filter((stage) => !oldStages.includes(stage));
        const typesToEnable = [];

        addedStages.forEach((stage) => {
            const { competitionType } = parseStageFilterKey(stage);
            if (
                competitionType &&
                !draftFilters.value.competition_types.includes(competitionType) &&
                !typesToEnable.includes(competitionType)
            ) {
                typesToEnable.push(competitionType);
            }
        });

        if (typesToEnable.length) {
            isEnablingCompetitionFromStage.value = true;
            draftFilters.value.competition_types = [
                ...draftFilters.value.competition_types,
                ...typesToEnable,
            ];
            nextTick(() => {
                isEnablingCompetitionFromStage.value = false;
            });
        }

        syncMatchIdsAfterStageChange(oldStages);
    }
);

watch(isDesktop, (desktop) => {
    if (desktop) {
        closeDrawer();
    }
});

function onCompetitionChange({ competitionTypes, stages }) {
    draftFilters.value.competition_types = [...competitionTypes];
    draftFilters.value.stages = [...stages];
}

function getFilteredMatchesForDraft(competitionTypes, stages) {
    return filterMatchesByDraft(props.matches, props.appliedFilters, {
        competition_types: competitionTypes,
        stages,
    });
}

function syncMatchIdsAfterFilterChange(oldCompetitionTypes, oldStageValues) {
    const oldMatches = getFilteredMatchesForDraft(oldCompetitionTypes, oldStageValues);
    const oldMatchIds = oldMatches.map((match) => match.id);
    const hadAllMatches =
        oldMatchIds.length > 0 && oldMatchIds.every((id) => draftFilters.value.match_ids.includes(id));

    if (hadAllMatches) {
        draftFilters.value.match_ids = filteredMatches.value.map((match) => match.id);
        return;
    }

    const allowed = filteredMatches.value.map((match) => match.id);
    draftFilters.value.match_ids = draftFilters.value.match_ids.filter((id) => allowed.includes(id));
}

function syncMatchIdsAfterStageChange(oldStages) {
    const oldMatches = getFilteredMatchesForDraft(draftFilters.value.competition_types, oldStages);
    const oldMatchIds = oldMatches.map((match) => match.id);
    const hadAllMatches =
        oldMatchIds.length > 0 && oldMatchIds.every((id) => draftFilters.value.match_ids.includes(id));

    if (hadAllMatches) {
        draftFilters.value.match_ids = filteredMatches.value.map((match) => match.id);
        return;
    }

    const allowed = filteredMatches.value.map((match) => match.id);
    draftFilters.value.match_ids = draftFilters.value.match_ids.filter((id) => allowed.includes(id));
}

function buildCompactFilters() {
    return compactFilterSelection(
        {
            ...draftFilters.value,
            season_ids: [...(props.appliedFilters.season_ids || draftFilters.value.season_ids)],
        },
        {
            teams: props.teams,
            positions: props.positions,
            matches: props.matches,
            appliedFilters: props.appliedFilters,
        }
    );
}

function applyFilters() {
    emit('apply', buildCompactFilters());
}

function resetFilters() {
    const defaults = getDefaultDraftFilters(props.seasons);
    const expanded = expandFilterSelection(defaults, {
        teams: props.teams,
        positions: props.positions,
        matches: props.matches,
        appliedFilters: defaults,
    });

    draftFilters.value = expanded;
    searchQuery.value = '';

    emit(
        'reset',
        compactFilterSelection(expanded, {
            teams: props.teams,
            positions: props.positions,
            matches: props.matches,
            appliedFilters: defaults,
        })
    );
}

function applyFiltersFromDrawer() {
    applyFilters();
    closeDrawer();
}

function resetFiltersFromDrawer() {
    resetFilters();
    closeDrawer();
}

function openDrawer() {
    isDrawerOpen.value = true;
    if (typeof document !== 'undefined') {
        document.body.style.overflow = 'hidden';
    }
}

function closeDrawer() {
    isDrawerOpen.value = false;
    if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
    }
}

function onEscape(event) {
    if (event.key === 'Escape' && isDrawerOpen.value) {
        closeDrawer();
    }
}

watch(isDrawerOpen, (open) => {
    if (typeof document === 'undefined') {
        return;
    }

    if (open) {
        document.addEventListener('keydown', onEscape);
    } else {
        document.removeEventListener('keydown', onEscape);
    }
});

onUnmounted(() => {
    if (typeof document === 'undefined') {
        return;
    }

    document.removeEventListener('keydown', onEscape);
    document.body.style.overflow = '';
});

function formatMatchDate(dateString) {
    if (!dateString) return '';
    if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    }
    return dateString;
}

function competitionLabel(type) {
    return COMPETITION_TYPES.find((item) => item.value === type)?.label || '';
}

function competitionIcon(type) {
    return COMPETITION_ICONS[type] || '';
}
</script>
